import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import { Header } from '../Layout/Header'
import { CheckCircle, Circle, Trash2, Plus, Clock, AlertTriangle, Bell, X } from 'lucide-react'

interface Task {
  id: string
  task: string
  completed: boolean
  scheduled_at?: string
  created_at: string
  created_by: string
  created_by_name: string
  assigned_to?: string
  assigned_to_name?: string
  completed_at?: string
  priority?: string
}

interface Machine {
  id: number
  name: string
  hal?: string
}

export const TeamLeaderDashboard: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<Task[]>([])
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [newTask, setNewTask] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  const [newTaskPriority, setNewTaskPriority] = useState<'laag' | 'normaal' | 'hoog'>('normaal')
  const [newTasksCount, setNewTasksCount] = useState(0)

  // Problem modal
  const [showProblemModal, setShowProblemModal] = useState(false)
  const [machines, setMachines] = useState<Machine[]>([])
  const [problemForm, setProblemForm] = useState({ productcode: '', machine_id: '', argumentatie: '', oplossing_gevonden: false })
  const [problemSubmitting, setProblemSubmitting] = useState(false)
  const [problemSuccess, setProblemSuccess] = useState(false)

  useEffect(() => {
    loadTasks()
    const subscription = supabase
      .channel('teamleader-tasks')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, (payload: any) => {
        loadTasks()
        if (payload.eventType === 'INSERT' && payload.new.assigned_to === user?.id) {
          setNewTasksCount(prev => prev + 1)
        }
      })
      .subscribe()
    return () => { subscription.unsubscribe() }
  }, [user?.id])

  useEffect(() => {
    if (showProblemModal && machines.length === 0) {
      supabase.from('machines').select('id, name, hal').order('name').then(({ data }) => setMachines(data || []))
    }
  }, [showProblemModal])

  const loadTasks = async () => {
    if (!user?.id) return
    const { data, error } = await supabase
      .from('tasks').select('*')
      .or(`assigned_to.eq.${user.id},created_by.eq.${user.id}`)
      .order('completed', { ascending: true })
      .order('priority', { ascending: false })
      .order('created_at', { ascending: false })
    if (!error) setTasks(data || [])
  }

  const addTask = async () => {
    if (!newTask.trim()) return
    const { error } = await supabase.from('tasks').insert([{
      task: newTask,
      completed: false,
      scheduled_at: scheduledTime ? new Date(`${new Date().toDateString()} ${scheduledTime}`).toISOString() : null,
      created_by: user?.id,
      created_by_name: user?.name || 'Onbekend',
      assigned_to: user?.id,
      assigned_to_name: user?.name,
      priority: newTaskPriority,
    }])
    if (!error) {
      setNewTask('')
      setScheduledTime('')
      setNewTaskPriority('normaal')
      setShowTaskForm(false)
      loadTasks()
    }
  }

  const toggleTask = async (taskId: string, currentStatus: boolean) => {
    await supabase.from('tasks').update({ completed: !currentStatus, completed_at: !currentStatus ? new Date().toISOString() : null }).eq('id', taskId)
    loadTasks()
  }

  const deleteTask = async (taskId: string) => {
    await supabase.from('tasks').delete().eq('id', taskId)
    loadTasks()
  }

  const handleLogout = () => { logout(); navigate('/login') }

  const submitProblem = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!problemForm.argumentatie.trim()) return
    setProblemSubmitting(true)
    try {
      const { error } = await supabase.from('probleem_meldingen').insert([{
        datum_tijd: new Date().toISOString(),
        productcode: problemForm.productcode,
        machine_id: problemForm.machine_id ? parseInt(problemForm.machine_id) : null,
        argumentatie: problemForm.argumentatie,
        oplossing_gevonden: problemForm.oplossing_gevonden,
        gebruiker_naam: user?.name || 'Onbekend',
        created_by: user?.id || 'unknown',
        created_by_name: user?.name || 'Onbekend',
      }])
      if (!error) {
        setProblemSuccess(true)
        setProblemForm({ productcode: '', machine_id: '', argumentatie: '', oplossing_gevonden: false })
        setTimeout(() => { setProblemSuccess(false); setShowProblemModal(false) }, 2000)
      }
    } finally {
      setProblemSubmitting(false)
    }
  }

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'hoog': return 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/10'
      case 'laag': return 'border-l-4 border-l-gray-300 bg-gray-50 dark:bg-gray-800'
      default: return 'border-l-4 border-l-yellow-400 bg-yellow-50 dark:bg-yellow-900/10'
    }
  }

  const getPriorityBadge = (priority?: string) => {
    switch (priority) {
      case 'hoog': return <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full dark:bg-red-900/40 dark:text-red-300">Urgent</span>
      case 'laag': return <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full dark:bg-gray-700 dark:text-gray-400">Laag</span>
      default: return null
    }
  }

  const pendingTasks = tasks.filter(t => !t.completed)
  const completedTasks = tasks.filter(t => t.completed)
  const urgentTasks = pendingTasks.filter(t => t.priority === 'hoog')
  const adminAssignedTasks = pendingTasks.filter(t => t.created_by !== user?.id)
  const today = new Date().toISOString().split('T')[0]
  const completedToday = completedTasks.filter(t => t.completed_at?.startsWith(today)).length

  return (
    <div className="min-h-screen flex flex-col items-center overflow-x-hidden" style={{ background: 'var(--bg)' }}>
      <Header title="Teamleider Dashboard" onLogout={handleLogout} />

      {/* Notification badge for new tasks */}
      {newTasksCount > 0 && (
        <div className="w-full max-w-3xl px-3 mt-3">
          <div className="flex items-center justify-between bg-yellow-400 text-black px-4 py-2 rounded-xl text-sm font-semibold animate-pulse">
            <div className="flex items-center gap-2">
              <Bell size={16} />
              {newTasksCount} nieuwe {newTasksCount === 1 ? 'taak' : 'taken'} toegewezen
            </div>
            <button onClick={() => setNewTasksCount(0)} className="p-1 hover:bg-yellow-500 rounded-lg transition">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <section className="w-full max-w-5xl px-3 sm:px-6 mt-6 sm:mt-8">
        <p className="eyebrow">Productie · teamleider</p>
        <h2 className="page-title mt-2">Goedendag, {user?.name?.split(' ')[0] || 'collega'}</h2>
        <p className="page-subtitle mt-1">Je dagplanning, urgente taken en productieacties in één overzicht.</p>
      </section>

      {/* Productivity stats */}
      <div className="w-full max-w-5xl px-3 sm:px-6 mt-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
          {[
            { label: 'Voltooid vandaag', value: completedToday, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
            { label: 'Open taken',       value: pendingTasks.length, color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
            { label: 'Urgent',           value: urgentTasks.length, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20' },
            { label: 'Van admin',        value: adminAssignedTasks.length, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
          ].map(stat => (
            <div key={stat.label} className="metric-card !block !p-3 sm:!p-4">
              <div className={`text-2xl sm:text-[28px] font-semibold tracking-[-0.03em] ${stat.color}`}>{stat.value}</div>
              <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Alert banners */}
      {(urgentTasks.length > 0 || adminAssignedTasks.length > 0) && (
        <div className="w-full max-w-5xl px-3 sm:px-6 mt-3 space-y-2">
          {urgentTasks.length > 0 && (
            <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-xl flex items-center gap-2 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">
              <AlertTriangle size={18} />
              <span className="font-semibold text-sm">{urgentTasks.length} urgente {urgentTasks.length === 1 ? 'taak' : 'taken'} vereist je aandacht!</span>
            </div>
          )}
          {adminAssignedTasks.length > 0 && (
            <div className="bg-blue-100 border border-blue-300 text-blue-800 px-4 py-3 rounded-xl flex items-center gap-2 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300">
              <Bell size={18} />
              <span className="text-sm">{adminAssignedTasks.length} {adminAssignedTasks.length === 1 ? 'taak toegewezen' : 'taken toegewezen'} door admin</span>
            </div>
          )}
        </div>
      )}

      {/* Task list */}
      <div className="w-full max-w-5xl px-3 sm:px-6 mt-4 mb-4">
        <section className="card p-4 sm:p-6">
          <div className="flex justify-between items-center border-b pb-3 mb-4" style={{ borderColor: 'var(--border)' }}>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Takenlijst ({pendingTasks.length} open)
            </h2>
            <button onClick={() => setShowTaskForm(!showTaskForm)}
              className="btn-primary !px-3">
              <Plus size={16} /> Taak
            </button>
          </div>

          {/* Add Task Form */}
          {showTaskForm && (
            <div className="mb-4 p-4 rounded-xl" style={{ background: 'var(--bg)' }}>
              <input
                type="text"
                value={newTask}
                onChange={e => setNewTask(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTask()}
                placeholder="Nieuwe taak invoeren..."
                className="input-field mb-2"
              />
              <input
                type="time"
                value={scheduledTime}
                onChange={e => setScheduledTime(e.target.value)}
                className="input-field mb-3"
              />
              {/* Priority toggle */}
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Prioriteit</label>
              <div className="flex rounded-lg overflow-hidden border mb-3" style={{ borderColor: 'var(--border)' }}>
                {([
                  { val: 'laag' as const, label: 'Laag', activeClass: 'bg-gray-500 text-white' },
                  { val: 'normaal' as const, label: 'Normaal', activeClass: 'bg-yellow-500 text-white' },
                  { val: 'hoog' as const, label: 'Hoog', activeClass: 'bg-red-600 text-white' },
                ]).map(opt => (
                  <button key={opt.val} type="button" onClick={() => setNewTaskPriority(opt.val)}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      newTaskPriority === opt.val ? opt.activeClass : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setShowTaskForm(false); setNewTask(''); setScheduledTime(''); setNewTaskPriority('normaal') }}
                  className="btn-secondary flex-1">Annuleren</button>
                <button onClick={addTask} className="btn-primary flex-1">Toevoegen</button>
              </div>
            </div>
          )}

          {/* Pending tasks */}
          <div className="space-y-2">
            {pendingTasks.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                <CheckCircle size={48} className="mx-auto mb-2 text-green-400" />
                <p>Alle taken voltooid!</p>
              </div>
            ) : (
              pendingTasks.map(task => (
                <div key={task.id} className={`p-3 rounded-xl transition-all hover:shadow-md ${getPriorityColor(task.priority)}`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => toggleTask(task.id, task.completed)} className="text-gray-400 hover:text-green-500 mt-0.5">
                      <Circle size={24} />
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{task.task}</span>
                        {getPriorityBadge(task.priority)}
                        {task.created_by !== user?.id && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full dark:bg-blue-900/40 dark:text-blue-300">
                            Van: {task.created_by_name}
                          </span>
                        )}
                      </div>
                      <div className="text-sm mt-1 flex items-center gap-3" style={{ color: 'var(--text-secondary)' }}>
                        {task.scheduled_at && (
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {new Date(task.scheduled_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        <span>{new Date(task.created_at).toLocaleDateString('nl-NL')}</span>
                      </div>
                    </div>
                    <button onClick={() => deleteTask(task.id)} className="text-gray-400 hover:text-red-500">
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Completed tasks */}
          {completedTasks.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm py-2" style={{ color: 'var(--text-secondary)' }}>
                {completedTasks.length} voltooide {completedTasks.length === 1 ? 'taak' : 'taken'} tonen
              </summary>
              <div className="mt-2 space-y-2">
                {completedTasks.map(task => (
                  <div key={task.id} className="p-3 rounded-xl bg-green-50 border-l-4 border-l-green-400 dark:bg-green-900/10">
                    <div className="flex items-start gap-3">
                      <button onClick={() => toggleTask(task.id, task.completed)} className="text-green-500 mt-0.5">
                        <CheckCircle size={24} />
                      </button>
                      <div className="flex-1">
                        <span className="line-through text-gray-500 dark:text-gray-400">{task.task}</span>
                        <div className="text-xs text-gray-400 mt-1">
                          Voltooid: {task.completed_at ? new Date(task.completed_at).toLocaleString('nl-NL') : 'Onbekend'}
                        </div>
                      </div>
                      <button onClick={() => deleteTask(task.id)} className="text-gray-400 hover:text-red-500">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-5xl px-3 sm:px-6 mb-8">
        <button onClick={() => setShowProblemModal(true)}
          className="btn-primary py-4 text-base font-semibold flex items-center justify-center gap-2">
          <AlertTriangle size={22} /> Probleem Melden
        </button>
        <button onClick={() => navigate('/product-inspection')}
          className="btn-primary py-4 text-base font-semibold flex items-center justify-center gap-2">
          <CheckCircle size={22} /> Productinspectie
        </button>
      </div>

      {/* Problem modal */}
      {showProblemModal && (
        <div className="modal-overlay" onClick={() => { setShowProblemModal(false); setProblemSuccess(false) }}>
          <div className="modal-content max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Probleem Melden</h3>
              <button onClick={() => { setShowProblemModal(false); setProblemSuccess(false) }}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                <X size={18} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            {problemSuccess ? (
              <div className="text-center py-8">
                <CheckCircle size={48} className="mx-auto mb-2 text-green-500" />
                <p className="font-semibold text-green-700 dark:text-green-400">Probleem gemeld!</p>
              </div>
            ) : (
              <form onSubmit={submitProblem} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Productcode</label>
                  <input className="input-field" value={problemForm.productcode} onChange={e => setProblemForm(p => ({ ...p, productcode: e.target.value }))} placeholder="Productcode (optioneel)" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Machine</label>
                  <select className="input-field" value={problemForm.machine_id} onChange={e => setProblemForm(p => ({ ...p, machine_id: e.target.value }))}>
                    <option value="">– Selecteer machine –</option>
                    {machines.map(m => (
                      <option key={m.id} value={m.id}>{m.name}{m.hal ? ` (${m.hal})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Probleemomschrijving *</label>
                  <textarea required className="input-field resize-none" rows={4} value={problemForm.argumentatie} onChange={e => setProblemForm(p => ({ ...p, argumentatie: e.target.value }))} placeholder="Beschrijf het probleem..." />
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="sol-found" checked={problemForm.oplossing_gevonden} onChange={e => setProblemForm(p => ({ ...p, oplossing_gevonden: e.target.checked }))} className="w-4 h-4 accent-red-700" />
                  <label htmlFor="sol-found" className="text-sm" style={{ color: 'var(--text-primary)' }}>Oplossing al gevonden</label>
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={() => setShowProblemModal(false)} className="btn-secondary flex-1">Annuleren</button>
                  <button type="submit" disabled={problemSubmitting} className="btn-primary flex-1 disabled:opacity-50">
                    {problemSubmitting ? 'Melden...' : 'Melding Indienen'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
